// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable } from '@angular/core';
import { LoadingStatus, needsLoading, Loaded, Role, MaybeLoadable, Loadable, GoAction } from '@app/core';
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  State,
  selectRoles,
  LoadingRolesAction,
  SelectRoleAction,
  RemoveRoleByIdAction,
  LoadingRoleDetailsAction,
  LoadingOrgRolesAction
} from '@app/access-management/state';
import { take, filter, map, tap } from 'rxjs/operators';

const shouldLoadRoles = (roles: MaybeLoadable) => needsLoading<Loaded<Role[]>>(roles);
const hasRoleState = (roles: MaybeLoadable) => !needsLoading<Loaded<Role[]>>(roles);

@Injectable({ providedIn: 'root' })
export class RolesFacadeService {
  roles$: Observable<Loaded<Role[]> | Loadable> = of();
  LOAD_COMPLETE = LoadingStatus.LOAD_COMPLETE;
  loading$: Observable<boolean> = of();
  loaded$: Observable<boolean> = of();
  loadFailed$: Observable<boolean> = of();

  constructor(private store: Store<State>) {}

  initialize() {
    const maybeRoles$ = this.store.pipe(select(selectRoles));

    maybeRoles$
      .pipe(
        take(1),
        filter(shouldLoadRoles)
      )
      .subscribe(() => this.store.dispatch(new LoadingRolesAction()));

    this.roles$ = maybeRoles$.pipe(filter<Loaded<Role[]> | Loadable>(hasRoleState));
    this.loading$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOADING));
    this.loaded$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOAD_COMPLETE));
    this.loadFailed$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOAD_FAILED));
  }

  loadOrganizationRoles(orgId: string) {
    const maybeRoles$ = this.store.pipe(select(selectRoles));

    maybeRoles$
      .pipe(
        take(1),
        filter(shouldLoadRoles)
      )
      .subscribe(() => this.store.dispatch(new LoadingOrgRolesAction(orgId)));

    this.roles$ = maybeRoles$.pipe(filter<Loaded<Role[]> | Loadable>(hasRoleState));
    this.loading$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOADING));
    this.loaded$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOAD_COMPLETE));
    this.loadFailed$ = this.roles$.pipe(map(roles => roles.status === LoadingStatus.LOAD_FAILED));
  }

  selectRole(role: any) {
    this.store.dispatch(new SelectRoleAction(role));
  }

  removeRoleById(roleId: string) {
    this.store.dispatch(new RemoveRoleByIdAction(roleId));
  }

  goToEditRole(row: any) {
    this.store.dispatch(new LoadingRoleDetailsAction(row.id));
    this.store.dispatch(new GoAction(['/iam/roles', row.id, 'edit']));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/state/roles.facade.ts